import React, {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { CardData, CardRewardsEarnRate, AdvancedCategory } from 'data/types/CardData';
import { CreditCardImage } from './CreditCardImage';
import { formatSpendCategory } from '../../data/shared/utils/formatSpendCategory';
import { formatEarnRate } from '../../data/shared/utils/formatEarnRate';
import { categoryEmojiMap } from '../../data/shared/utils/CategoryEmojiMap.web';
import { formatRewardsType } from 'data/shared/utils/formatRewardsType';
import { generateDisclaimers } from '../../utils/generateDisclaimers';
import {
  isSelectableCategoriesEarnRate,
  isQuarterlyCategoriesEarnRate,
  isFixedCategoryGroupEarnRate,
  isAdvancedCategory,
} from '../../data/types/utils';
import { getCurrentQuarter } from '../../data/shared/hooks/useBestEarnRatesV2';

// Helper function to get category emoji
const getCategoryEmoji = (
  category: string
): React.ReactNode | string | undefined => {
  // Try exact match first
  if (categoryEmojiMap[category]) {
    return categoryEmojiMap[category];
  }

  // Try parent categories by removing segments from right to left
  const segments = category.split('.');
  while (segments.length > 1) {
    segments.pop();
    const parentCategory = segments.join('.');
    if (categoryEmojiMap[parentCategory]) {
      return categoryEmojiMap[parentCategory];
    }
  }

  // Return fallback if no match found
  return '📦';
};

interface CardRewardsTooltipProps {
  card: CardData;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  delay?: number;
}

export const CardRewardsTooltip: React.FC<CardRewardsTooltipProps> = ({
  card,
  children,
  position = 'top',
  delay = 300,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [computedPosition, setComputedPosition] = useState<'top' | 'bottom'>(
    'top'
  );
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Build rewards summary similar to CardDetailsPanel
  const rewardsSummaryItems = useMemo(() => {
    if (!card.rewards_earn_rates || card.rewards_earn_rates.length === 0) {
      return [];
    }

    // Filter out rates that require travel portal (consistent with useBestEarnRatesV2 and other components)
    const filteredRates = card.rewards_earn_rates.filter(
      (rate) => !rate.requiresTravelPortal
    );

    // Flatten rates while preserving the raw rate for disclaimers
    type FlattenedRate = {
      category: string;
      earnRate: number;
      rawRate: CardRewardsEarnRate;
      isSelectable?: boolean;
      isQuarterly?: boolean;
      isFixedGroup?: boolean;
    };

    const flattenedRates: FlattenedRate[] = [];
    const currentQuarter = getCurrentQuarter();

    filteredRates.forEach((rate) => {
      if (isSelectableCategoriesEarnRate(rate)) {
        // Use first category for display
        if (rate.selectableCategories.categories.length > 0) {
          const firstCategory = rate.selectableCategories.categories[0];
          const categoryName =
            typeof firstCategory === 'string'
              ? firstCategory
              : firstCategory.categories?.[0] || 'Unknown';

          flattenedRates.push({
            category: categoryName,
            earnRate: rate.earnRate,
            rawRate: rate,
            isSelectable: true,
          });
        }
      } else if (isQuarterlyCategoriesEarnRate(rate)) {
        rate.selectableCategories.categories
          ?.filter((cat: string | AdvancedCategory) =>
            typeof cat !== 'string' && cat.active_quarter === currentQuarter
          )
          ?.forEach((cat: string | AdvancedCategory) => {
            const categories = isAdvancedCategory(cat) ? cat.categories : [cat as string];
            categories.forEach((category: string) => {
              flattenedRates.push({
                category,
                earnRate: rate.earnRate,
                rawRate: rate,
                isQuarterly: true,
              });
            });
          });
      } else if (isFixedCategoryGroupEarnRate(rate)) {
        rate.selectableCategories.categories.forEach((cat: string | AdvancedCategory) => {
          const categories = isAdvancedCategory(cat) ? cat.categories : [cat as string];
          categories.forEach((category: string) => {
            flattenedRates.push({
              category,
              earnRate: rate.earnRate,
              rawRate: rate,
              isFixedGroup: true,
            });
          });
        });
      } else {
        flattenedRates.push({
          category: rate.category,
          earnRate: rate.earnRate,
          rawRate: rate,
        });
      }
    });

    // Filter and sort rates - only show bonus rates (> 1x)
    // Also check for both "allPurchases" and "all_purchases" category names
    const bonusRates = flattenedRates
      .filter((rate) => {
        if (!rate.earnRate || !rate.category) {
          return false;
        }
        // Show if it's a bonus rate (> 1x) OR if it's allPurchases/all_purchases (even if 1x, as it's the base rate)
        return (
          rate.earnRate > 1 ||
          rate.category === 'allPurchases' ||
          rate.category === 'all_purchases'
        );
      })
      .sort((a, b) => (b.earnRate || 0) - (a.earnRate || 0))
      .slice(0, 6); // Limit to top 6 categories

    return bonusRates
      .filter((rate) => rate.category && rate.earnRate)
      .map((rate) => {
        // Generate compact disclaimers
        const disclaimers = generateDisclaimers(rate.rawRate, { compact: true });

        return {
          category: rate.category,
          earnRate: rate.earnRate,
          isSelectable: rate.isSelectable,
          isQuarterly: rate.isQuarterly,
          isFixedGroup: rate.isFixedGroup,
          disclaimers,
        };
      });
  }, [card.rewards_earn_rates]);

  const handleMouseEnter = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
    setTooltipStyle({}); // Reset tooltip style
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  // Recalculate position when tooltip becomes visible (for auto positioning)
  useLayoutEffect(() => {
    if (isVisible && containerRef.current && tooltipRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      // Calculate position based on mode
      let effectivePosition = position;

      if (position === 'auto') {
        // Measure tooltip after it's rendered
        const tooltipHeight = tooltipRect.height > 0 ? tooltipRect.height : 300;
        const spaceAbove = containerRect.top;
        const spaceBelow = window.innerHeight - containerRect.bottom;

        if (spaceBelow >= tooltipHeight + 20 || spaceBelow > spaceAbove) {
          effectivePosition = 'bottom';
          setComputedPosition('bottom');
        } else {
          effectivePosition = 'top';
          setComputedPosition('top');
        }
      } else {
        setComputedPosition(
          position === 'top' || position === 'bottom' ? position : 'top'
        );
        effectivePosition = position;
      }

      // Calculate fixed position coordinates
      const containerCenterX = containerRect.left + containerRect.width / 2;
      let top = 0;
      let left = 0;
      let transform = '';

      if (effectivePosition === 'top') {
        // Position above
        top = containerRect.top - tooltipRect.height - 12;
        left = containerCenterX;
        transform = 'translateX(-50%)';
      } else if (effectivePosition === 'bottom') {
        // Position below
        top = containerRect.bottom + 12;
        left = containerCenterX;
        transform = 'translateX(-50%)';
      } else if (effectivePosition === 'left') {
        top = containerRect.top + containerRect.height / 2;
        left = containerRect.left - tooltipRect.width - 12;
        transform = 'translateY(-50%)';
      } else {
        // right
        top = containerRect.top + containerRect.height / 2;
        left = containerRect.right + 12;
        transform = 'translateY(-50%)';
      }

      // Set fixed positioning
      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        transform,
        zIndex: 10000,
        pointerEvents: 'none',
        opacity: 1,
      });
    }
  }, [isVisible, position]);

  const getPositionStyles = (): React.CSSProperties => {
    // If tooltipStyle is set (for portal/fixed positioning), use it
    if (Object.keys(tooltipStyle).length > 0) {
      return tooltipStyle;
    }

    // Fallback to absolute positioning for initial render (hidden until positioned)
    const baseStyles: React.CSSProperties = {
      position: 'absolute',
      zIndex: 10000,
      pointerEvents: 'none',
      opacity: 0,
      visibility: 'hidden',
    };

    // Use computed position for auto mode
    const effectivePosition = position === 'auto' ? computedPosition : position;

    switch (effectivePosition) {
      case 'top':
        return {
          ...baseStyles,
          bottom: 'calc(100% + 12px)',
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'bottom':
        return {
          ...baseStyles,
          top: 'calc(100% + 12px)',
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'left':
        return {
          ...baseStyles,
          right: 'calc(100% + 12px)',
          top: '50%',
          transform: 'translateY(-50%)',
        };
      case 'right':
        return {
          ...baseStyles,
          left: 'calc(100% + 12px)',
          top: '50%',
          transform: 'translateY(-50%)',
        };
      default:
        return baseStyles;
    }
  };

  const tooltipContent = isVisible ? (
    <div ref={tooltipRef} style={getPositionStyles()}>
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          padding: '16px',
          minWidth: '280px',
          maxWidth: '320px',
          border: '1px solid #e5e7eb',
        }}
      >
        {/* Card Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
            paddingBottom: '12px',
            borderBottom: '1px solid #f3f4f6',
          }}
        >
          <CreditCardImage
            imageName={card.imageName}
            alt={card.name}
            width={60}
            maxHeight={40}
            style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              borderRadius: '6px',
              flexShrink: 0,
            }}
          />
          <h3
            style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 500,
              fontFamily: 'Merriweather, serif',
              color: '#000',
              lineHeight: 1.3,
              flex: 1,
            }}
          >
            {card.name}
          </h3>
        </div>

        {/* Rewards Summary */}
        {rewardsSummaryItems.length > 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
              }}
            >
              {card.rewardsType
                ? `Earn ${formatRewardsType(card.rewardsType)}`
                : 'Rewards summary'}
            </div>
            {rewardsSummaryItems.map((rate, index) => (
              <div
                key={`${rate.category}-${index}`}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#EEF1F4',
                  color: '#333',
                  borderRadius: '8px',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: '14px' }}>
                  {getCategoryEmoji(rate.category)}
                </span>
                <span style={{ fontWeight: 500 }}>
                  {formatEarnRate(rate.earnRate, card.rewardsType)}
                </span>
                <span style={{ color: '#5D6C70' }}>
                  {formatSpendCategory(rate.category)}
                </span>
                {rate.disclaimers.length > 0 && (
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      marginLeft: 'auto',
                    }}
                  >
                    {rate.disclaimers.join(', ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              fontSize: '14px',
              color: '#9ca3af',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '8px 0',
            }}
          >
            No bonus rewards available
          </div>
        )}
      </div>
      {/* Arrow pointer */}
      <div
        style={{
          position: 'absolute',
          width: 0,
          height: 0,
          borderStyle: 'solid',
          ...((position === 'auto' ? computedPosition : position) === 'top'
            ? {
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderWidth: '8px 8px 0 8px',
                borderColor: '#fff transparent transparent transparent',
              }
            : (position === 'auto' ? computedPosition : position) === 'bottom'
            ? {
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                borderWidth: '0 8px 8px 8px',
                borderColor: 'transparent transparent #fff transparent',
              }
            : (position === 'auto' ? computedPosition : position) === 'left'
            ? {
                left: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                borderWidth: '8px 0 8px 8px',
                borderColor: 'transparent transparent transparent #fff',
              }
            : {
                right: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                borderWidth: '8px 8px 8px 0',
                borderColor: 'transparent #fff transparent transparent',
              }),
        }}
      />
    </div>
  ) : null;

  return (
    <>
      <div
        ref={containerRef}
        style={{ position: 'relative', display: 'inline-block' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
};
